import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop } from 'helpers/GlobalHelper';
import { isFoxSoulResetPasswordPage } from 'helpers/RouteHelper';
import OverlayFooter from 'components/OverlayFooter';
import Header from '../Header';

import Style from './ResetPassword.module.scss';

const ResetPassword = ({
  autoFocus,
  headerProps,
  language,
  onFieldChange,
  onResetPassword,
  resetPasswordError,
  resetPasswordPassword,
  resetPasswordPasswordError,
  resetPasswordShowPassword,
  resetPasswordConfirmPassword,
  resetPasswordConfirmPasswordError,
  resetPasswordShowConfirmPassword,
}) => {
  const {
    profile_passwordRequirementsText: pwRequirementText,
    profileResetPw_button: passwordButtonText,
    profileResetPw_invalidResetCodeMessage: invalidResetCodeText,
    profileChangePw_header: headerText,
  } = language || {};
  const passwordType = resetPasswordShowPassword ? 'text' : 'password';
  const passwordConfirmType = resetPasswordShowConfirmPassword ? 'text' : 'password';
  const buttonClass =
    resetPasswordPassword && resetPasswordConfirmPassword
      ? classNames(Style.btnPrimary, Style.filledButton)
      : Style.btnPrimary;
  let passwordErrorClass;
  let passwordErrorText;
  let passwordNewPasswordErrorClass;
  let passwordConfirmPasswordErrorClass;
  let resetPasswordInput = React.createRef();
  let resetPasswordConfirmInput = React.createRef();

  const invalidTokenErrorText =
    isFoxSoulResetPasswordPage && resetPasswordError === 'token is not valid'
      ? invalidResetCodeText
      : resetPasswordError;

  if (resetPasswordError) {
    passwordErrorClass = Style.defaultError;
    passwordNewPasswordErrorClass = classNames(Style.defaultError, Style.inputFieldError);
    passwordConfirmPasswordErrorClass = classNames(Style.defaultError, Style.inputFieldError);
  }

  if (resetPasswordPasswordError) {
    passwordErrorText = pwRequirementText;
    passwordErrorClass = Style.defaultError;
    passwordNewPasswordErrorClass = classNames(Style.defaultError, Style.inputFieldError);
  }

  if (resetPasswordConfirmPasswordError) {
    passwordErrorText = pwRequirementText;
    passwordErrorClass = Style.defaultError;
    passwordConfirmPasswordErrorClass = classNames(Style.defaultError, Style.inputFieldError);
  }

  return (
    <div className={classNames(Style.resetPassword, isFoxSoulResetPasswordPage && Style.foxSoul)}>
      <Header {...headerProps} />
      <div className={Style.resetPasswordContainer}>
        <h1>{headerText}</h1>
        <div className={Style.resetPasswordFormContainer}>
          <div className={Style.wrap}>
            <input
              autoFocus={autoFocus}
              className={classNames(Style.inputField, passwordNewPasswordErrorClass)}
              type={passwordType}
              data-testid="reset-page-new-password"
              name="resetPasswordPassword"
              value={resetPasswordPassword}
              onChange={onFieldChange}
              ref={input => {
                resetPasswordInput = input;
              }}
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <label
              className={classNames(
                Style.floatingLabel,
                resetPasswordPassword && Style.floatingLabelFilled,
              )}
            >
              New Password
            </label>
            <label className={classNames(Style.passwordLabel, passwordErrorClass)}>
              <input
                className={classNames(Style.resetPasswordShowPassword, passwordErrorClass)}
                name="resetPasswordShowPassword"
                type="checkbox"
                value={resetPasswordShowPassword}
                onChange={e => {
                  onFieldChange(e);
                  resetPasswordInput.focus();
                }}
              />
              <span />
            </label>
          </div>
          <div className={Style.wrap}>
            <input
              className={classNames(Style.inputField, passwordConfirmPasswordErrorClass)}
              type={passwordConfirmType}
              data-testid="reset-page-confirm-password"
              name="resetPasswordConfirmPassword"
              value={resetPasswordConfirmPassword}
              onChange={onFieldChange}
              ref={input => {
                resetPasswordConfirmInput = input;
              }}
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <label
              className={classNames(
                Style.floatingLabel,
                resetPasswordConfirmPassword && Style.floatingLabelFilled,
              )}
            >
              Confirm Password
            </label>
            <label className={classNames(Style.passwordLabel, passwordErrorClass)}>
              <input
                className={classNames(Style.resetPasswordShowPassword, passwordErrorClass)}
                name="resetPasswordShowConfirmPassword"
                type="checkbox"
                value={resetPasswordShowConfirmPassword}
                onChange={e => {
                  onFieldChange(e);
                  resetPasswordConfirmInput.focus();
                }}
              />
              <span />
            </label>
            <div className={Style.error}>{passwordErrorText || invalidTokenErrorText}</div>
          </div>
          <div className={Style.resetPasswordInfo}>{pwRequirementText}</div>
          <div className={Style.resetPasswordButton}>
            <button onClick={onResetPassword} className={buttonClass}>
              {passwordButtonText}
            </button>
          </div>
        </div>
      </div>
      <OverlayFooter language={language} />
    </div>
  );
};

ResetPassword.defaultProps = {
  autoFocus: false,
  language: {},
  network: [],
  onFieldChange: noop,
  onResetPassword: noop,
  resetPasswordPassword: '',
  resetPasswordPasswordError: null,
  resetPasswordShowPassword: false,
  resetPasswordConfirmPassword: '',
  resetPasswordConfirmPasswordError: null,
  resetPasswordShowConfirmPassword: false,
};

ResetPassword.propTypes = {
  autoFocus: PropTypes.bool,
  headerProps: PropTypes.shape({}).isRequired,
  language: PropTypes.shape({}),
  network: PropTypes.arrayOf(PropTypes.shape({})),
  onFieldChange: PropTypes.func,
  onResetPassword: PropTypes.func,
  resetPasswordEmail: PropTypes.string,
  resetPasswordEmailError: PropTypes.bool,
  resetPasswordError: PropTypes.string,
  resetPasswordPassword: PropTypes.string,
  resetPasswordPasswordError: PropTypes.bool,
  resetPasswordShowPassword: PropTypes.bool,
  resetPasswordConfirmPassword: PropTypes.string,
  resetPasswordConfirmPasswordError: PropTypes.bool,
  resetPasswordShowConfirmPassword: PropTypes.bool,
};

export default ResetPassword;
